// Long-form case study content, keyed by `c{companyIndex}p{projectIndex}`
// (0-based indices into the `companies` array in ./companies.js).
// Each entry supplies the copy for the six case-study sections:
// Overview, Background, Problems, Design, Solutions, Outcome.
import thriveIntro from "../assets/ehs/thrive_cs_intro.png";
import thrivePages from "../assets/ehs/thrive_pages.png";
import thriveSiteMap from "../assets/ehs/thrive_sitemap.png";
import rasfIntro from "../assets/ehs/rasf/rasf_intro.png";
import catalogIntro from "../assets/ehs/catalog/catalog_intro.png";
import catalogDetails from "../assets/ehs/catalog/catalog_productDetails.png";
import catalogMB from "../assets/ehs/catalog/catalog_productMobileVersion.png";
import ffAppIntro from "../assets/foodfight/ff_cs_intro.png";
import ffOverAll from "../assets/foodfight/ff_overall.png";
import ffProcess from "../assets/foodfight/ff_fightProcess.png";
import ffComponent from "../assets/foodfight/ff_components.png";
import ffNavbar from "../assets/foodfight/ff_navbar.png";
import cosIntro from "../assets/cos/cos_intro.png";
import cosBudget from "../assets/cos/cos_budget.png";
import cosSitemap from "../assets/cos/cos_sitemap.png";
import cosAB from "../assets/cos/cos_abTesting.png";
import cosIdeation from "../assets/cos/cos_ideation.png";
import gaugeIntro from "../assets/gauge/gauge_intro.png";
import gaugeMarket from "../assets/gauge/gauge_market.png";
import gaugeSeet from "../assets/gauge/gauge_sheets.png";
import gaugeBranding from "../assets/gauge/gauge_research.svg";
import gaugeVersions from "../assets/gauge/gauge_versions.png";
import kitchrIntro from "../assets/kitchr/kitchr_intro";
import kitchrStory from "../assets/kitchr/kitchr_story.png";
import kitchrProduct from "../assets/kitchr/kitchr_product.png";
import kitchrSketch from "../assets/kitchr/kitchr_sketch.png";
import kitchrDiagram from "../assets/kitchr/kitchr_digram.png";

const caseStudyContent = {
  c0p0: {
    overviewTitle: "Intro",
    introImage: thriveIntro,
    introwidth: "100%",
    overview:
      "EHS Thrive is a complimentary intranet that connects more than 106 members across 10 areas of expertise and leadership. Since 2023, EHS has been outlining expectations and development plans to strategically cultivate the next generation of leaders. However, we had multiple platforms, programs, and development plans available, which can be challenging for end-users to find details and progress. In 2025, Safety Training launched the idea to connect these dots and make information readily available for every member of the organization.",
    backgroundTitle: "Roles",
    backgroundProse:
      "<b>In the initial phase</b>, I collaborated with four other researchers to conduct user interviews, background research, and competitor analysis. After that, I led the design and development of the project.\n\n <b>The research phase</b> took about one and a half months, and the first iteration lasted approximately three weeks. After 2-3 more iterations, the project was approved and rolled out organization-wide in August 2025.",
    problemsTitle: "Research and Goals",
    problemsProse:
      "The project began with background research, competitive analysis, and user interviews (n=15), revealing three key pain points: <b>91% of users preferred a one-stop platform</b> over decentralized channels, 73% found awareness and discoverability overly complex, and 64% felt overwhelmed by email volume. While early ideas surfaced, the team lacked a clear purpose and value proposition. After months of research and stakeholder discussions, <b>I led the design strategy with a clear goal: connect people with the information most critical to their growth and success, aligned with the organization's leadership profile.</b>",
    problems: [
      {
        l: 1,
        t: "Single source of truth",
        b: "Create a centralized digital experience that serves as the single source of truth for users.",
      },
      {
        l: 2,
        t: "Clear content display",
        b: "Present the most relevant information through clear, focused, and user-centered content display.",
      },
      {
        l: 3,
        t: "Community & engagement",
        b: "Enhance information architecture, workflows, and functionality to support members' development, improve engagement, and foster a stronger sense of community.",
      },
    ],
    designTitle: "Design Overview",
    designProse: "",
    design: [
      {
        t: "IA-C received 64% of votes",
        b: "Consolidated content that was previously scattered across multiple locations into a single, streamlined sitemap. The selection offers fewer clicks to key information, event-centered navigation, easier onboarding for new or occasional users, and clearer separation of content types.",
      },
      {
        t: "Montserrat + U-M palette",
        b: "Followed U-M branding guidelines throughout, selecting Montserrat for readability and approachability while retaining the university's color palette. Subtle patterns and accents reinforce hierarchy and key messaging.",
      },
      {
        t: "Right information first",
        b: "With a high volume of content to manage, we prioritized surfacing the most important information first. Each feature module serves a distinct purpose, making it easy for users to engage with each section.",
      },
    ],
    progimg1: thrivePages,
    prodWidth1: "100%",
    progimg2: thriveSiteMap,
    prodWidth2: "100%",
    prodLocked2: true,
    prodImgShow3: "none",
    prodImgShow4: "none",
    designProse2:
      "Built on v2's success to drive a more comprehensive redesign — improving information architecture, revising the UI, and integrating AI features based on stakeholder and user feedback.",
    solutionsTitle: "Limitations and Overcoming",
    solutions: [
      {
        l: "",
        t: "Google Sites",
        b: "Although we had a team full of talented people, and I could have built the site using React.js or Drupal, building a website with a complex solution can be hard to maintain, difficult to get a buy-in, and can take a lot of time. Given these constraints, we evaluated several solutions and chose Google Sites as the most practical platform at the time.",
      },
      {
        l: "",
        t: "Custom embedded components",
        b: "Google Sites is a limited platform in terms of flexibility; however, its structure supports accessibility, and its built-in responsiveness simplifies implementation. To preserve key aspects of our design, we strategically embedded custom components where necessary. The embedded elements were implemented using simple HTML with lightweight JavaScript functions. I also documented their use cases and implications to ensure the next maintainer could easily understand.",
      },
    ],
    outcomeTitle: "Impact Summary",
    outcome:
      "The redesigned platform significantly improved user acquisition and engagement, demonstrating that users are not only finding the site more accessible but also spending more time meaningfully interacting with relevant content.",
    outcomes: [
      {
        l: 1,
        t: "85% increase in site visits 200 days after launch",
        b: "Compared to the previous version, which had incomplete integration and a less defined goal structure.",
      },
      {
        l: 2,
        t: "Nearly doubled user interactions, increasing from 2,300 to 4,100 event counts",
        b: "indicating significantly higher engagement with key content and features.",
      },
      {
        l: 3,
        t: "Average session duration increased from 2.4 minutes to 8.4 minutes",
        b: "While maintaining strong return user retention, showing that users are spending more time meaningfully engaging with the platform.",
      },
    ],
  },
  c0p1: {
    overviewTitle: "Intro",
    introImage: rasfIntro,
    overview:
      "The University of Michigan housed over 100 labs with approximately 4,000 researchers. Keeping safety information straightforward and accessible helps reduce avoidable accidents, saves lives, reduces the cost of repairs, and keeps studies continuing smoothly. In February 2026, EHS hosted the Research & Academic Safety Fair (RASF) again after 4 years. On this occasion, I proudly led the research and design for the event website and marketing materials, which connect target audiences with the organization efficiently.",
    backgroundTitle: "Roles",
    backgroundProse:
      "This project took about 2 months from November, 2025 to January, 2026. I led the research and design from background research, competitor analysis, stakeholders' semi-interviews, and A&B testing. Meanwhile, I also designed and delivered an event information hub that translated complex event logistics, speaker content, and venue information into a clear, accessible experience. Using the university's existing platform and design system enabled rapid deployment.",
    problemsTitle: "Research and Goals",
    problemsProse:
      "This is a campus-wide scale event costing approximately $120,000, including contingency budget, where cost efficiency, audience coverage, and spreading a safety culture awareness have been at the forefront of the project. With 5 competitive analysis and stakeholders semi-interviews, we concluded that in 2026, RASF should achieve:",
    problems: [
      {
        l: "1",
        t: "Spread awareness",
        b: "Spread awareness and grow the audience engagement.",
      },
      {
        l: "2",
        t: "Organized & impactful",
        b: "Keep information organized and impactful for clear communication.",
      },
      {
        l: "3",
        t: "Increase conversions",
        b: "Increase conversions from marketing campaigns to registrations.",
      },
    ],
    designTitle: "Design Overview",
    designProse: "",
    design: [
      {
        t: "Clarity above all",
        b: "The primary design objective was clarity. Content was structured around attendees' information needs, moving from event overview and logistics to program details, speaker information, and venue navigation.",
      },
      {
        t: "Design Consistency",
        b: "The site followed the university design system and visual identity standards, carrying institutional colors throughout while using white space strategically to maintain readability and visual balance.",
      },
      {
        t: "Separation of concerns",
        b: "Speaker details separated from event overview. Cards used to scan speaker information. Maps separated from agenda content. Diagrams used where visuals communicated faster than text.",
      },
    ],
    solutionsTitle: "What we delivered",
    solutions: [
      {
        t: "Event information hub: Translated complexity into clarity",
        b: "I designed and delivered an event information hub that translated complex event logistics, speaker content, and venue information into a clear, accessible experience using the university's existing platform and design system.",
      },
      {
        t: "Rapid deployment: Built on existing infrastructure",
        b: "Using the university's existing platform and design system enabled rapid deployment while maintaining consistency with institutional standards and reducing time-to-launch significantly.",
      },
    ],
    outcomeTitle: "Impact Summary",
    outcome:
      "1. Built and delivered an event information platform that converted 76% of visitors into registrants.\n2. Achieved a 93% positive usefulness rating among users.\n3. Delivered internally at approximately one-quarter of the estimated outsourced marketing cost.",
  },
  c0p2: {
    overviewTitle: "Intro",
    introImage: catalogIntro,
    overview:
      "Environmental Health & Safety (EHS) offers more than 40 required training courses to 4,000-5,000 researchers, staff, and students across diverse laboratory and shop environments. Course requirements are typically communicated through Principal Investigators (PIs), supervisors, and EHS specialists. However, users frequently need additional information about course content, enrollment procedures, prerequisites, and schedule changes.\n\nA review of support requests revealed that 23.8% of inquiries were related to course details and schedule updates, information that users could potentially access independently if it were presented clearly and centrally. To address this gap, I proposed and designed a centralized course catalog experience that improves discoverability, transparency, and self-service access to training information.",
    backgroundTitle: "Roles",
    backgroundProse:
      "Lead UX Designer & Researcher\n\n- Identified opportunities through support-ticket analysis and stakeholder discussions.\n- Conducted competitive analysis across 5-6 comparable training and catalog systems.\n- Performed background research and communication-flow analysis to understand how training information is distributed.\n- Defined the information architecture, user flows, content strategy, and interaction patterns.\n- Created wireframes, UI components, and high-fidelity prototypes.\n- Facilitated discussions with stakeholders and aligned the solution with organizational and university standards.",
    problemsTitle: "Problems",
    problemsProse:
      "Users frequently need additional information about course content, enrollment procedures, prerequisites, and schedule changes that is not readily available in a single place.",
    problems: [
      {
        l: "1",
        t: "Fragmented information",
        b: "23.8% of support inquiries were related to course details and schedule updates — information users could access independently if presented clearly and centrally.",
      },
      {
        l: "2",
        t: "Poor discoverability",
        b: "No centralized catalog meant users had to search across disconnected resources, guessing keywords and spending 20-40 minutes on tasks that should take seconds.",
      },
      {
        l: "3",
        t: "No self-service access",
        b: "Without a centralized experience, users were dependent on PIs, supervisors, and EHS specialists for basic training information.",
      },
    ],
    designTitle: "Design",
    designProse:
      "The solution is a centralized, user-friendly course catalog built around quick access to training information and updates. Key design decisions included:",
    design: [
      {
        t: "Discoverability: Organized for real needs",
        b: "Prioritizing training announcements, events, and schedule changes on the homepage. Organizing courses by training type, job responsibility, and work environment. Providing filtering and search tools to help users quickly locate relevant courses.",
      },
      {
        t: "Navigation & detail: Persistent sidebar + rich pages",
        b: "Using a persistent sidebar navigation to maintain orientation and reduce cognitive load. Creating detailed course pages with prerequisites, related courses, downloadable resources, articles, and accessibility features such as voice-over content.",
      },
      {
        t: "Personalization & community: Signed-in experiences",
        b: "Designing personalized experiences for signed-in users, including training expiration tracking, recommended learning pathways, and simplified contact/request actions. Supporting community engagement through reviews, experience sharing, and direct feedback channels.",
      },
    ],
    progimg1: catalogDetails,
    prodWidth1: "100%",
    progimg2: catalogMB,
    prodWidth2: "100%",
    prodLocked2: false,
    prodImgShow3: "none",
    prodImgShow4: "none",
    solutionsTitle: "Outcome",
    solutions: [
      {
        t: "Prototype impact: Significantly faster task completion",
        b: "The prototype demonstrated a clear path toward reducing information-seeking friction. Compared with the existing platform, users could locate course information significantly faster, potentially saving 20-40 minutes previously spent searching across disconnected resources and guessing keywords.",
      },
      {
        t: "Stakeholder response: Positive buy-in",
        b: "Stakeholders responded positively to the concept and recognized the value of a centralized, up-to-date training catalog. While the project remains in progress and has not yet launched, the prototype established a shared vision for future development.",
      },
    ],
    outcomeTitle: "Outcome",
    outcome:
      "The prototype demonstrated a clear path toward reducing information-seeking friction and improving self-service access to training resources. Compared with the existing platform, users could locate course information significantly faster, potentially saving 20-40 minutes previously spent searching across disconnected resources and guessing keywords.\n\nStakeholders responded positively to the concept and recognized the value of a centralized, up-to-date training catalog. While the project remains in progress and has not yet launched, the prototype established a shared vision for future development and provided a foundation for gaining organizational buy-in and validating impact through future adoption and usage metrics.",
  },
  c0p3: {
    overviewTitle: "Intro",
    overview:
      "Emergency response information was traditionally distributed through physical flip charts placed throughout laboratories and workspaces. While familiar to users, the format was limited by location, difficult to maintain, and inaccessible to many digital and assistive technology users.\n\nThe goal of this project was to transform the emergency flip chart into an accessible digital experience while preserving the familiarity and simplicity of the original format.",
    backgroundTitle: "Roles",
    backgroundProse:
      "UX Designer & Accessibility Advocate\n\n- Audited existing emergency response content and workflows.\n- Restructured information for digital delivery.\n- Evaluated technical solutions for creating an accessible flip-chart experience.\n- Designed navigation, layouts, and interaction patterns.\n- Collaborated with stakeholders to ensure compliance with accessibility requirements and emergency communication standards.",
    problemsTitle: "Problems",
    problemsProse:
      "A key challenge was balancing familiarity with accessibility. Users were accustomed to navigating information through physical flip charts, but traditional page-flipping interactions are often difficult for screen readers and keyboard-only users.",
    problems: [
      {
        l: "Problem 01",
        t: "Limited by location",
        b: "The physical format was limited by location — only useful to people who happened to be physically near a flip chart during an emergency.",
      },
      {
        l: "Problem 02",
        t: "Difficult to maintain",
        b: "Physical flip charts were difficult to maintain and keep up to date across all laboratory and workspace locations.",
      },
      {
        l: "Problem 03",
        t: "Inaccessible",
        b: "The format was inaccessible to many digital and assistive technology users, and traditional page-flipping interactions are often difficult for screen readers and keyboard-only users.",
      },
    ],
    designTitle: "Design",
    designProse:
      "To address this, I designed a digital flip-chart experience using an accessible JavaScript library and structured content to support assistive technologies. The interface preserved the recognizable page-based navigation while ensuring compatibility with screen readers, keyboard navigation, and responsive devices.\n\nAdditional design considerations included:",
    design: [
      {
        l: "Information hierarchy",
        t: "Clear hierarchy for emergencies",
        b: "Clear information hierarchy for rapid access during emergency situations — the most critical information is immediately visible without requiring navigation.",
      },
      {
        l: "Accessibility",
        t: "Accessible labels & structure",
        b: "Accessible labels, semantic structure, and keyboard interactions throughout — built using an accessible JavaScript library to ensure compatibility with assistive technologies.",
      },
      {
        l: "Architecture",
        t: "Scalable content architecture",
        b: "Scalable content architecture to support future updates and maintenance, ensuring the platform can evolve without requiring a complete rebuild.",
      },
    ],
    solutionsTitle: "Outcome",
    solutions: [
      {
        l: "Digital experience",
        t: "Preserved familiarity, added accessibility",
        b: "The prototype demonstrated a modern, accessible alternative to physical emergency flip charts. By preserving a familiar interaction model while supporting assistive technologies, the design improved access to critical safety information across devices and user abilities.",
      },
      {
        l: "Foundation built",
        t: "Core requirement, not afterthought",
        b: "The project established a foundation for future digital emergency communication initiatives and highlighted accessibility as a core requirement rather than an afterthought.",
      },
    ],
    outcomeTitle: "Outcome",
    outcome:
      "The prototype demonstrated a modern, accessible alternative to the organization's physical emergency flip charts. By preserving a familiar interaction model while supporting assistive technologies, the design improved access to critical safety information across devices and user abilities.\n\nThe project established a foundation for future digital emergency communication initiatives and highlighted accessibility as a core requirement rather than an afterthought.",
  },
  c0p4: {
    overviewTitle: "Intro",
    overview:
      "As part of an ongoing effort to improve online training experiences, our team evaluated courses delivered through DominKnow Flow, an e-learning authoring platform used across the university. Training participants ranged from researchers and technical professionals to employees with limited day-to-day computer use, creating diverse expectations and levels of digital literacy.\n\nUsing platform analytics and user feedback, we identified opportunities to improve navigation, content comprehension, visual consistency, and message visibility across the learning experience.",
    backgroundTitle: "Roles",
    backgroundProse:
      "UX Designer & Learning Experience Designer\n\n- Analyzed user behavior and platform usage data.\n- Participated in workshops, brainstorming sessions, and design reviews with a five-person team.\n- Evaluated learning experiences across multiple courses.\n- Contributed to interaction, visual, and content design improvements.\n- Collaborated with stakeholders to validate proposed solutions through testing and feedback sessions.",
    problemsTitle: "Problems",
    problemsProse:
      "Using platform analytics and user feedback, we identified opportunities to improve across the learning experience.",
    problems: [
      {
        l: "Problem 01",
        t: "Navigation confusion",
        b: "Users experienced navigation confusion between lesson sections, with no consistent patterns to guide them through course material.",
      },
      {
        l: "Problem 02",
        t: "Content comprehension",
        b: "Content comprehension suffered from inconsistent layouts and poor visual hierarchy, making it hard to distinguish critical information.",
      },
      {
        l: "Problem 03",
        t: "Visual inconsistency",
        b: "Visual inconsistency and poor message visibility across courses created a fragmented experience that did not align with the university design system.",
      },
    ],
    designTitle: "Design",
    designProse:
      "The project focused on creating a more intuitive and consistent learning experience for users with varying levels of technical proficiency. Key design objectives included:\n\nImproving navigation and reducing confusion between lesson sections. Increasing content readability and information digestibility. Aligning visual presentation with the university design system. Highlighting critical messages and actions more effectively. Creating consistent interaction patterns across courses to reduce learning friction.\n\nThe team explored multiple design directions, applying and testing solutions across approximately 12 courses. Through iterative reviews, A/B testing, and stakeholder feedback sessions, we refined layouts, content presentation, and navigation structures to support smoother progression through training materials.",
    design: [
      {
        l: "Navigation",
        t: "Reduce confusion between sections",
        b: "Redesigned navigation patterns reduced confusion between lesson sections and created a consistent progression model that learners could rely on across all courses.",
      },
      {
        l: "Readability",
        t: "Content digestibility",
        b: "Refined layouts increased content readability and information digestibility, highlighting critical messages and actions more effectively for learners at all levels.",
      },
      {
        l: "Consistency",
        t: "University design system",
        b: "Visual presentation was aligned with the university design system, creating consistent interaction patterns across approximately 12 courses to reduce learning friction.",
      },
    ],
    solutionsTitle: "Outcome",
    solutions: [
      {
        l: "Testing & validation",
        t: "Clear preference demonstrated",
        b: "Testing demonstrated a clear preference for the redesigned layouts and navigation patterns, with stakeholders reporting improved content flow and smoother transitions between lessons.",
      },
      {
        l: "Scalable framework",
        t: "Applied across future courses",
        b: "The resulting design recommendations created a scalable framework that can be applied across future courses while supporting learners with a broad range of digital literacy levels.",
      },
    ],
    outcomeTitle: "Outcome",
    outcome:
      "The project established a more consistent and user-friendly learning experience across the training platform. Testing demonstrated a clear preference for the redesigned layouts and navigation patterns, with stakeholders reporting improved content flow and smoother transitions between lessons.\n\nThe resulting design recommendations created a scalable framework that can be applied across future courses while supporting learners with a broad range of digital literacy levels.",
  },
  c1p0: {
    overviewTitle: "Intro",
    introImage: ffAppIntro,
    overview:
      "FoodFight connects sports fans through interactive games centered on friendly competition, food, and drinks. Users predict game outcomes, with the losing rival covering the food bill.\n\nI joined FoodFight Inc. as a UX Developer from November 2024 to October 2025, following an internship from May to August 2023. I collaborated closely with engineering, marketing, and product strategy teams, contributing to the Version 2.1 app overhaul with end-to-end improvements across both B2C and B2B platforms.\n\n <b>Type:</b> Professional Engagement\n <b>Project Duration:</b> 14 months total\n <b>Tools:</b> Figma, FigJam, HTML, CSS, JavaScript, Google Workspace, Photoshop\n <b>Roles:</b> Lead Designer, Design Engineer, Project Manager",
    backgroundTitle: "Roles & Responsibilities",
    backgroundProse:
      "I led the end-to-end UX process, from strategy and design through development, delivering cohesive experiences across the mobile app, consumer website, and B2B desktop web application.\n\nI also developed reusable UI components that complemented engineering implementations and helped define and scale the product's design system, including core components such as cards, carousels, and other foundational UI patterns.",
    problemsTitle: "Challenges & Goals",
    problemsProse:
      "Most consumer apps separate sports engagement from food ordering, often relying on third-party platforms such as Uber Eats. FoodFight intentionally unifies these experiences into a single, continuous user flow.",
    problems: [
      {
        l: "1",
        t: "Cross-platform consistency",
        b: "Ensure cross-platform consistency to reduce cognitive load across complex, multi-step interactions.",
      },
      {
        l: "2",
        t: "Re-architect for speed and Clarity",
        b: "Re-architect app flows for speed and clarity while surfacing supporting features through research-driven design.",
      },
      {
        l: "3",
        t: "Documentation & guidelines for scalibility",
        b: "Establish documentation and design guidelines to ensure long-term scalability and team alignment.",
      },
    ],
    designTitle: "Product",
    designProse:
      "A product that balances powerful complexity behind the scenes with a clean, intuitive experience, offering over 16 thoughtfully designed features.",
    design: [
      {
        t: "Key actions within thumb reach",
        b: "Key actions are placed within thumb reach, with a clear information hierarchy for effortless scanning.",
      },
      {
        t: "Subtle status indicators",
        b: "Subtle status indicators reduce cognitive load and clarify progress through thoughtful in-app messaging.",
      },
      {
        t: "Informed by user testing",
        b: "Navbar placement informed by internal reviews and user testing to ensure feature accessibility.",
      },
    ],
    progimg1: ffProcess,
    prodWidth1: "80%",
    progimg2: ffOverAll,
    prodWidth2: "80%",
    prodImgShow3: "none",
    prodImgShow4: "none",
    solutionsTitle: "Solutions",
    solutions: [
      {
        img: ffComponent,
        t: "Keep Users on Track",
        b: "Subtle status indicators reduce cognitive load and clarify progress through thoughtful in-app messaging.",
      },
      {
        img: ffNavbar,
        t: "Well-Researched Navigation",
        b: "Navbar placement informed by internal reviews and user testing to ensure feature accessibility.",
      },
    ],
    outcomeTitle: "Result",
    outcome:
      "After more than six sprints, FoodFight launched Version 2.1 on both the App Store and Google Play. Designs were validated with real users at each stage and refined through usability testing and market feedback.\n\nThis process strengthened my experience in cross-functional collaboration, scalable system design, and fast-paced product development.",
  },
  c1p1: {
    overviewTitle: "Coming soon",
    overview: "This case study is currently being written. Check back soon.",
    backgroundTitle: "Background",
    backgroundProse: "",
    problemsTitle: "Problems",
    problemsProse: "",
    problems: [],
    designTitle: "Design",
    designProse: "",
    design: [],
    solutionsTitle: "Solutions",
    solutions: [],
    outcomeTitle: "Outcome",
    outcome: "",
  },
  c3p0: {
    overviewTitle: "Intro",
    introImage: cosIntro,
    overview:
      "Through interviews and usability feedback with young professionals, supported by secondary research, I identified key pain points in managing everyday finances and translated them into clear, actionable features. Iterative design cycles improved clarity across core financial tasks, created a more intuitive budgeting experience, and established scalable design components. While this was an early-stage product concept, it was grounded in real user input and designed to mirror early-stage product constraints, strengthening my ability to deliver research-informed solutions that prioritize speed, accuracy, and usability.\n\nType: Early-Stage Product Concept\nProject Duration: 16 Weeks\nTools: Figma, Figjam, Photoshop, Illustrator, HTML, JS, CSS, Google Suite\nRoles: Lead Designer, UX Researcher, Design Engineer",
    backgroundTitle: "Roles & Responsibilities",
    backgroundProse:
      "Designed an end-to-end UX solution for a finance-focused product, covering research, information architecture, user flows, and scalable design systems. Research insights informed interaction design and prototyping, which were iterated through usability testing and stakeholder feedback. I also built coded demos to validate complex interactions, with a focus on clarity, accuracy, and efficient task completion.",
    problemsTitle: "Challenges & Goals",
    problemsProse:
      "The goal was to design an accessible, user-centered experience that simplifies complex financial workflows, leverages AI-driven guidance, and encourages positive financial behaviors. Insights from user interviews highlighted several core challenges that informed the design direction.",
    problems: [
      {
        l: "From User Perspective",
        t: "Platform Complexity",
        b: "Managing multiple accounts across different platforms creates confusion and slows down financial tasks, making everyday money management time-consuming and error-prone.",
      },
      {
        l: "From User Perspective",
        t: "Stressful Steps",
        b: "High cognitive load and financial stress prevent users from focusing on personal goals, creativity, and long-term planning, reducing confidence in their financial decisions.",
      },
      {
        l: "From Design Perspective",
        t: "Balancing Speed and Security",
        b: "Users want fast financial actions but also need to feel their money is secure. I designed UI components and security steps to strike a balance that felt both quick and trustworthy.",
      },
    ],
    designTitle: "Product",
    designProse:
      "Cos provides proactive support that keeps users engaged, helping them resolve issues quickly and manage their finances with confidence. The experience is designed to be smooth, goal-oriented, and focused on reducing stress in everyday financial decisions.\n\n Help Center: Available 24/7, combining real-person support with AI assistance to guide users whenever they need help solving issues.\nTroubleshooting: Quickly resolves technical or OS-specific issues so users can manage their finances seamlessly across devices.\nAI Assistance: Offers personalized insights, planning guidance, and recommendations — all in one app — helping users make confident financial decisions.",
    design: [
      {
        l: "System-level challenge",
        t: "Slow digital workflows",
        b: "Financial tasks should feel faster than traditional ATMs and as seamless as swiping a credit card.",
      },
      {
        l: "System-level challenge",
        t: "Backend overloads",
        b: "Excessive fetch requests or server bottlenecks can disrupt flows and lead to app crashes.",
      },
      {
        l: "System-level challenge",
        t: "Lengthy verification",
        b: "Manual errors and slow verification processes slow users down; technology should support, not replace, them.",
      },
    ],
    prodImgShow1: "none",
    prodImgShow2: "none",
    prodImgShow3: "none",
    prodImgShow4: "none",
    solutionsTitle: "Ideation & Wireframes",
    solutions: [
      {
        l: "Ideation",
        t: "Data and connection issues",
        b: "Issues arise from data control and gaps in communication between the system and users. Most users rely on their data on banking mobile apps, which could be a problem when the internet is down.",
      },
      {
        l: "Wireframes",
        t: "A/B tested directions",
        b: "After discussing scenarios, identifying problems and opportunities, and incorporating stakeholder feedback, we translated these insights into wireframes. We conducted A/B testing to evaluate which version our target focus group found easier and faster to use.",
      },
    ],
    outcomeTitle: "Result",
    outcome:
      "Through research with young professionals and multiple design iterations, we identified key pain points and defined core features that addressed real user needs. The process improved clarity in financial tasks, created a more intuitive budgeting experience, and established scalable design components. While conceptual, the project strengthened my skills in UX research, user-centered design, and dashboard-focused workflows for financial apps.",
  },
  c3p1: {
    overviewTitle: "Intro",
    overview:
      "Gauge is a research-driven conceptual project focused on helping small and medium businesses manage their finances more intuitively. The project was informed by interviews with eight small business owners, uncovering recurring pain points and user preferences often overlooked by complex, tech-centric financial software. While the product was not built, the project strengthened my understanding of small business finance and directly prepared me for delivering user-centered solutions on subsequent client projects.\n\nType: Concept Study\nProject Duration: 12 Weeks\nTools: Figma, FigJam, Photoshop, Illustrator\nRoles: Lead Designer, UX Researcher",
    backgroundTitle: "Roles & Responsibilities",
    backgroundProse:
      "- Conducted semi-structured interviews with small business owners to understand financial workflows, pain points, and goals.\n- Synthesized research findings using affinity mapping to identify patterns, prioritize user needs, and inform design decisions.\n- Translated insights into design solutions, progressing from wireframes to high-fidelity prototypes and iterating based on user feedback.\n- Designed with a focus on scalability and reusable components to support future product growth.",
    problemsTitle: "Challenges & Goals",
    problemsProse:
      "This project aimed to design flexible, simplified, and AI-assisted workflows that adapt to different skill levels, enabling SMB owners to discover solutions intuitively rather than conforming to rigid, tech-centric software.",
    problems: [
      {
        l: "Challenge 01",
        t: "Digital Literacy",
        b: "Wide variance in digital and financial literacy among small and medium business owners makes onboarding and feature discovery difficult.",
      },
      {
        l: "Challenge 02",
        t: "Teamwork",
        b: "Multi-user team accounts introduce complexity, leading to misinterpretation of workflows and reduced confidence in adoption.",
      },
      {
        l: "Challenge 03",
        t: "Complicated Products",
        b: "Overly complex SaaS interfaces discourage exploration, even when the underlying tools could improve efficiency.",
      },
    ],
    designTitle: "Product",
    designProse:
      "Gauge helps small and medium businesses manage financial data across multiple sources, including POS systems, spreadsheets, and manual records, so owners can focus on growth rather than operational complexity.",
    design: [
      {
        l: "Feature 01",
        t: "Collaborative by Design",
        b: "Delegate financial tasks with role-based access and resolve issues efficiently through asynchronous collaboration.",
      },
      {
        l: "Feature 02",
        t: "Clear, Actionable Insights",
        b: "Monitor financial health at a glance through a centralized dashboard and evaluate priorities with AI-assisted recommendations.",
      },
      {
        l: "Feature 03",
        t: "Credit & Business Connections",
        b: "Improve cash-flow visibility through credit insights and partner integrations using connected business data.",
      },
    ],
    solutionsTitle: "Research",
    solutions: [
      {
        l: "Rinse & Repeat",
        t: "Ideation, Interpretation, Integration",
        b: "To develop solutions, we iterated through a three-step process: Ideation, Interpretation, and Integration, ensuring each cycle informed the next.",
      },
      {
        l: "Key findings",
        t: "Simplicity attracts small businesses",
        b: "SaaS Misperception: Businesses often believed improving financial and digital literacy would be difficult and time-consuming, which discouraged product adoption. Staying Informed: Keeping users notified of the latest updates allows adequate time for business planning. Simplicity: As functionality becomes more complex, products must remain simple and intuitive.",
      },
    ],
    outcomeTitle: "Result",
    outcome:
      "Through interviews with eight small businesses and three design iterations, we defined seven core features addressing real user needs. The process improved workflow clarity, created a more intuitive financial experience, and established a scalable component system. While conceptual, this project strengthened my understanding of small B2B finance, user-centered design, interviews, and dashboard-focused workflows for future client projects.",
  },
  c2p0: {
    overviewTitle: "Intro",
    introImage: cosIntro,
    overview:
      "Through interviews and usability feedback with young professionals, supported by secondary research, I identified key pain points in managing everyday finances and translated them into clear, actionable features. Iterative design cycles improved clarity across core financial tasks, created a more intuitive budgeting experience, and established scalable design components. While this was an early-stage product concept, it was grounded in real user input and designed to mirror early-stage product constraints, strengthening my ability to deliver research-informed solutions that prioritize speed, accuracy, and usability.\n\n <b>Type:</b> Early-Stage Product Concept\n <b>Project Duration:</b> 16 Weeks\n <b>Tools:</b> Figma, Figjam, Photoshop, Illustrator, HTML, JS, CSS, Google Suite\n <b>Roles:</b> Lead Designer, UX Researcher, Design Engineer",
    backgroundTitle: "Roles & Responsibilities",
    backgroundProse:
      "Designed an end-to-end UX solution for a finance-focused product, covering research, information architecture, user flows, and scalable design systems. Research insights informed interaction design and prototyping, which were iterated through usability testing and stakeholder feedback. I also built coded demos to validate complex interactions, with a focus on clarity, accuracy, and efficient task completion.",
    problemsTitle: "Challenges & Goals",
    problemsProse:
      "The goal was to design an accessible, user-centered experience that simplifies complex financial workflows, leverages AI-driven guidance, and encourages positive financial behaviors. Insights from user interviews highlighted several core challenges that informed the design direction.",
    problems: [
      {
        l: "1",
        t: "Platform Complexity",
        b: "Managing multiple accounts across different platforms creates confusion and slows down financial tasks, making everyday money management time-consuming and error-prone.",
      },
      {
        l: "2",
        t: "Stressful Steps",
        b: "High cognitive load and financial stress prevent users from focusing on personal goals, creativity, and long-term planning, reducing confidence in their financial decisions.",
      },
      {
        l: "3",
        t: "Balancing Speed and Security",
        b: "Users want fast financial actions but also need to feel their money is secure. I designed UI components and security steps to strike a balance that felt both quick and trustworthy.",
      },
    ],
    designTitle: "Product",
    designProse:
      "Cos provides proactive support that keeps users engaged, helping them resolve issues quickly and manage their finances with confidence. The experience is designed to be smooth, goal-oriented, and focused on reducing stress in everyday financial decisions.\n\n Help Center: Available 24/7, combining real-person support with AI assistance to guide users whenever they need help solving issues. Troubleshooting: Quickly resolves technical or OS-specific issues so users can manage their finances seamlessly across devices. AI Assistance: Offers personalized insights, planning guidance, and recommendations—all in one app—helping users make confident financial decisions.",
    design: [
      {
        t: "Slow digital workflows",
        b: "Financial tasks should feel faster than traditional ATMs and as seamless as swiping a credit card.",
      },
      {
        t: "Backend overloads",
        b: "Excessive fetch requests or server bottlenecks can disrupt flows and lead to app crashes.",
      },
      {
        t: "Lengthy verification",
        b: "Manual errors and slow verification processes slow users down; technology should support, not replace, them.",
      },
    ],
    progimg1: cosBudget,
    prodWidth1: "70%",
    progimg2: cosSitemap,
    prodWidth2: "92%",
    prodImgShow3: "none",
    prodImgShow4: "none",
    solutionsTitle: "Ideation & Wireframes",
    solutions: [
      {
        img: cosIdeation,
        l: "Ideation",
        t: "Data and connection issues",
        b: "Issues arise from data control and gaps in communication between the system and users. Most users rely on their data on banking mobile apps, which could be a problem when the internet is down.",
      },
      {
        img: cosAB,
        l: "Wireframes",
        t: "A/B tested directions",
        b: "After discussing scenarios, identifying problems and opportunities, and incorporating stakeholder feedback, we translated these insights into wireframes. We conducted A/B testing to evaluate which version our target focus group found easier and faster to use.",
      },
    ],
    solutionImg1: cosIdeation,
    outcomeTitle: "Result",
    outcome:
      "Through research with young professionals and multiple design iterations, we identified key pain points and defined core features that addressed real user needs. The process improved clarity in financial tasks, created a more intuitive budgeting experience, and established scalable design components. While conceptual, the project strengthened my skills in UX research, user-centered design, and dashboard-focused workflows for financial apps.",
  },
  c2p1: {
    overviewTitle: "Intro",
    introImage: gaugeIntro,
    overview:
      "Gauge is a research-driven conceptual project focused on helping small and medium businesses manage their finances more intuitively. The project was informed by interviews with eight small business owners, uncovering recurring pain points and user preferences often overlooked by complex, tech-centric financial software. While the product was not built, the project strengthened my understanding of small business finance and directly prepared me for delivering user-centered solutions on subsequent client projects.\n\nType: Concept Study\nProject Duration: 12 Weeks\nTools: Figma, FigJam, Photoshop, Illustrator\nRoles: Lead Designer, UX Researcher",
    backgroundTitle: "Roles & Responsibilities",
    backgroundProse:
      "- Conducted semi-structured interviews with small business owners to understand financial workflows, pain points, and goals.\n- Synthesized research findings using affinity mapping to identify patterns, prioritize user needs, and inform design decisions.\n- Translated insights into design solutions, progressing from wireframes to high-fidelity prototypes and iterating based on user feedback.\n- Designed with a focus on scalability and reusable components to support future product growth.",
    problemsTitle: "Challenges & Goals",
    problemsProse:
      "This project aimed to design flexible, simplified, and AI-assisted workflows that adapt to different skill levels, enabling SMB owners to discover solutions intuitively rather than conforming to rigid, tech-centric software.",
    problems: [
      {
        l: "1",
        t: "Digital Literacy",
        b: "Wide variance in digital and financial literacy among small and medium business owners makes onboarding and feature discovery difficult.",
      },
      {
        l: "2",
        t: "Teamwork",
        b: "Multi-user team accounts introduce complexity, leading to misinterpretation of workflows and reduced confidence in adoption.",
      },
      {
        l: "3",
        t: "Complicated Products",
        b: "Overly complex SaaS interfaces discourage exploration, even when the underlying tools could improve efficiency.",
      },
    ],
    designTitle: "Product",
    designProse:
      "Gauge helps small and medium businesses manage financial data across multiple sources, including POS systems, spreadsheets, and manual records, so owners can focus on growth rather than operational complexity.",
    design: [
      {
        t: "Collaborative by Design",
        b: "Delegate financial tasks with role-based access and resolve issues efficiently through asynchronous collaboration.",
      },
      {
        t: "Clear, Actionable Insights",
        b: "Monitor financial health at a glance through a centralized dashboard and evaluate priorities with AI-assisted recommendations.",
      },
      {
        t: "Credit & Business Connections",
        b: "Improve cash-flow visibility through credit insights and partner integrations using connected business data.",
      },
    ],
    progimg1: gaugeMarket,
    progimg2: gaugeSeet,
    progimg3: gaugeBranding,
    progimg4: gaugeVersions,
    // prodWidth1: "100%",
    solutionsTitle: "Research",
    solutions: [
      {
        t: "Ideation, Interpretation, Integration",
        b: "To develop solutions, we iterated through a three-step process: Ideation, Interpretation, and Integration, ensuring each cycle informed the next.",
      },
      {
        t: "Simplicity attracts small businesses",
        b: "SaaS Misperception: Businesses often believed improving financial and digital literacy would be difficult and time-consuming, which discouraged product adoption. Staying Informed: Keeping users notified of the latest updates allows adequate time for business planning. Simplicity: As functionality becomes more complex, products must remain simple and intuitive.",
      },
    ],
    outcomeTitle: "Result",
    outcome:
      "Through interviews with eight small businesses and three design iterations, we defined seven core features addressing real user needs. The process improved workflow clarity, created a more intuitive financial experience, and established a scalable component system. While conceptual, this project strengthened my understanding of small B2B finance, user-centered design, interviews, and dashboard-focused workflows for future client projects.",
  },
  c2p2: {
    overviewTitle: "Intro",
    introImage: kitchrIntro,
    overview:
      "Kitchr is a concept study exploring how local food communities can reduce waste, improve access to affordable meals, and foster social connection through shared cooking and grocery coordination.\n\n<b>Type:</b> Concept Study\n<b>Project Duration:</b> 14 Weeks\n<b>Tools:</b> Figma, FigJam, Miro, Photoshop, Illustrator\n<b>Roles:</b> Product Design, Design & Research Lead",
    backgroundTitle: "Challenges & Approach",
    backgroundProse:
      "The core challenge addressed in this project was the imbalance between food affordability and food waste. While many individuals struggle to access affordable, diverse meals, large amounts of food are discarded due to inefficient distribution and limited community coordination.\n\nKitchr explored this gap by enabling people to share grocery purchases, cooking spaces, and meals locally. Early concepts focused on university-age users, then expanded to broader urban and suburban communities experiencing similar access and waste challenges.",
    problemsTitle: "Problems",
    problemsProse:
      "The imbalance between food affordability and food waste creates real challenges for communities.",
    problems: [
      {
        l: "1",
        t: "Food affordability",
        b: "Many individuals struggle to access affordable, diverse meals, especially in urban and suburban communities with limited resources.",
      },
      {
        l: "2",
        t: "Food waste",
        b: "Large amounts of food are discarded due to inefficient distribution and limited community coordination around purchasing and cooking.",
      },
      {
        l: "3",
        t: "Limited social connection",
        b: "Existing food apps focus on individual consumption rather than community building, missing an opportunity for shared experiences and trust.",
      },
    ],
    designTitle: "Product",
    designProse: "",
    design: [
      {
        t: "Shop and Cook Locally",
        b: "Encouraging shared grocery shopping and communal cooking to strengthen local food ecosystems.",
      },
      {
        t: "Flexible Cost Sharing",
        b: "Users can reduce annual food expenses by coordinating purchases and meals with trusted local connections.",
      },
      {
        t: "Safety and Trust",
        b: "Community trust is supported through verification steps, transparent profiles, and clear expectations around food safety.",
      },
    ],
    progimg1: kitchrStory,
    progimg2: kitchrProduct,
    progimg3: kitchrSketch,
    progimg4: kitchrDiagram,
    solutionsTitle: "Research",
    solutions: [
      {
        l: "Storyboarding",
        t: "Key intervention points",
        b: "Storyboards explored how users might respond to common food-access challenges both with and without Kitchr, helping identify key intervention points.",
      },
      {
        l: "Persona Study",
        t: "Three primary personas",
        b: "Three primary personas were developed, including students and young professionals seeking greater food variety, affordability, and social engagement.",
      },
    ],
    outcomeTitle: "Testing & Result",
    outcome:
      "Alternative task flows were explored to ensure the final concept addressed the broader problem space rather than a single user scenario. User flows were structured to prioritize trust, safety, and convenience, reflecting common concerns around food sharing.\n\nEarly sketches focused on mapping potential user flows before validating assumptions. Layouts were refined through low-fidelity wireframes before transitioning to high-fidelity designs in Figma. Rapid ideation enabled exploration of multiple concepts and accelerated convergence on a cohesive solution.",
  },
};

export default caseStudyContent;
